import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { format, differenceInMonths, eachMonthOfInterval, eachWeekOfInterval, 
         differenceInDays, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { Group } from '@visx/group';
import { useDarkMode } from '../../../context/DarkModeContext';
import { TIMELINE_COLORS } from '../../../constants/timeline';
import useUserTimezone from '../../../hooks/useUserTimezone';

interface TimelineGridProps {
  startDate: Date;
  endDate: Date;
  width: number;
  height: number;
  xScale: (date: Date) => number;
}

const TimelineGrid: React.FC<TimelineGridProps> = React.memo(({
  startDate,
  endDate,
  width,
  height,
  xScale
}) => {
  const { darkMode } = useDarkMode();
  const { formatDateOnlyWithTimezone } = useUserTimezone();
  const [shouldRotateLabels, setShouldRotateLabels] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  
  // Format date in user's timezone
  const formatDateForTimeline = (date: Date, formatStr: string): string => {
    // For timeline specific formatting needs, we'll still use date-fns format
    // but we'll convert the date to the user's timezone first
    return format(date, formatStr);
  };
  
  useEffect(() => {
    // Detect mobile device once on mount
    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobileDevice(mobile);
  }, []);
  
  // Generate time markers based on zoom level - only recalculate when dates change
  const timeMarkers = useMemo(() => {
    const visibleMonths = differenceInMonths(endDate, startDate);
    const visibleDays = differenceInDays(endDate, startDate);
    let markers = [];

    if (visibleMonths > 24) {
      // Show years
      const years = new Set();
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const year = currentDate.getFullYear();
        if (!years.has(year)) {
          years.add(year);
          markers.push({
            date: new Date(year, 0, 1),
            label: year.toString()
          });
        }
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
    } else if (visibleMonths > 2) {
      // Show months
      markers = eachMonthOfInterval({
        start: startOfMonth(startDate),
        end: endOfMonth(endDate)
      }).map(date => ({
        date,
        label: formatDateForTimeline(date, 'MMM yyyy')
      }));
    } else if (visibleDays > 14) {
      // Show weeks with more frequent dates
      markers = eachWeekOfInterval({
        start: startOfWeek(startDate),
        end: endOfWeek(endDate)
      }).map(date => ({
        date,
        label: formatDateForTimeline(date, 'MMM d')
      }));

      // Add mid-week markers
      const midWeekMarkers = eachWeekOfInterval({
        start: startOfWeek(addDays(startDate, 3)),
        end: endOfWeek(endDate)
      }).map(date => ({
        date: addDays(date, 3),
        label: formatDateForTimeline(addDays(date, 3), 'MMM d')
      }));
      
      markers = [...markers, ...midWeekMarkers].sort((a, b) => 
        a.date.getTime() - b.date.getTime()
      );
    } else {
      // For very zoomed in views (2 weeks or less), show every 2-3 days
      const interval = Math.max(Math.floor(visibleDays / 8), 2); // Show at least 8 markers, minimum 2 days apart
      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        markers.push({
          date: new Date(currentDate),
          label: formatDateForTimeline(currentDate, 'MMM d')
        });
        currentDate = addDays(currentDate, interval);
      }
    }

    return markers;
  }, [startDate, endDate, formatDateForTimeline]);
  
  // Check if labels would overlap
  const checkLabelRotation = useCallback(() => {
    const isNarrowScreen = window.innerWidth < 768;
    
    // Always rotate on very narrow screens
    if (width < 350) {
      setShouldRotateLabels(true);
      return;
    }
    
    // Calculate average label width - estimate based on characters
    const avgCharWidth = 7; // approx 7px per character at font-size 12px
    
    // Calculate average label length
    const avgLabelLength = timeMarkers.reduce((sum, marker) => 
      sum + marker.label.length, 0) / Math.max(1, timeMarkers.length);
    
    const estimatedLabelWidth = avgLabelLength * avgCharWidth;
    
    // Calculate average spacing between markers
    let totalSpacing = 0;
    let spacingPoints = 0;
    
    // Only calculate spacing between visible markers
    const visibleMarkers = timeMarkers.filter(marker => {
      const x = xScale(marker.date);
      return x >= 0 && x <= width;
    });
    
    for (let i = 0; i < visibleMarkers.length - 1; i++) {
      const currentX = xScale(visibleMarkers[i].date);
      const nextX = xScale(visibleMarkers[i + 1].date);
      totalSpacing += (nextX - currentX);
      spacingPoints++;
    }
    
    const avgSpacing = spacingPoints > 0 ? totalSpacing / spacingPoints : width;
    
    // Rotate if estimated label width is greater than 80% of available space
    // or if we're on mobile and labels are close
    setShouldRotateLabels(
      (estimatedLabelWidth > avgSpacing * 0.8) || 
      (isNarrowScreen && avgSpacing < 70)
    );
  }, [timeMarkers, width, xScale]);
  
  // Detect if labels need to be rotated based on width
  useEffect(() => {
    checkLabelRotation();
    
    // Add resize listener to update rotation when window is resized
    const handleResize = () => {
      checkLabelRotation();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [checkLabelRotation]);

  // Calculate the bottom position for labels, ensuring they stay within the container
  const labelBottomPosition = Math.min(height - 25, Math.max(height * 0.85, 400));
  
  // Get appropriate colors based on dark mode
  const colors = useMemo(() => {
    return {
      gridLine: darkMode ? "rgba(150,150,150,0.15)" : "rgba(0,0,0,0.1)",
      monthLine: darkMode ? "rgba(150,150,150,0.4)" : "rgba(0,0,0,0.2)",
      text: darkMode ? "#CCCCCC" : "#000000"
    };
  }, [darkMode]);

  // Fixed position for the text labels - increased vertical offset to avoid overlap with axis
  const labelYPosition = labelBottomPosition + 20; // Increased from +5 to +20

  return (
    <Group>
      {/* Main axis line */}
      <line
        x1={0}
        y1={labelBottomPosition - 15}
        x2={width}
        y2={labelBottomPosition - 15}
        stroke={colors.monthLine}
        strokeWidth={1}
      />
      {/* Background grid lines and labels */}
      {timeMarkers.map(({ date, label }) => {
        const x = xScale(date);
        // Only render markers that are within the visible area (with buffer)
        if (x < -50 || x > width + 50) return null;
        
        // Calculate additional adjustment for long labels to prevent overflow
        const isLongLabel = label.length > 6;
        
        return (
          <g key={date.toISOString()}>
            {/* Vertical grid line */}
            <line
              x1={x}
              y1={0}
              x2={x}
              y2={labelBottomPosition - 20}
              stroke={colors.gridLine}
              strokeWidth={1}
              strokeDasharray="4,4"
              opacity={0.9}
            />
            {/* Tick mark */}
            <line
              x1={x}
              y1={labelBottomPosition - 20}
              x2={x}
              y2={labelBottomPosition - 15}
              stroke={colors.monthLine}
              strokeWidth={2}
            />
            {/* Date label */}
            {shouldRotateLabels ? (
              // Rotated label - using SVG transformation for proper alignment
              // Adjusted horizontal position by 4px to precisely align with tick mark
              (<g transform={`translate(${x + 2}, ${labelYPosition}) rotate(-90)`}>
                <text
                  x={0}
                  y={0}
                  dominantBaseline="middle"
                  textAnchor="middle"
                  fill={colors.text}
                  fontSize={12}
                  fontWeight="500"
                  fontFamily="system-ui, -apple-system, sans-serif"
                  className="timeline-label font-medium"
                  style={{ 
                    userSelect: 'none',
                    WebkitTextSizeAdjust: 'none',
                    MozTextSizeAdjust: 'none'
                  }}
                >
                  {label}
                </text>
              </g>)
            ) : (
              // Horizontal label
              (<text
                x={x}
                y={labelYPosition}
                dominantBaseline="middle"
                textAnchor="middle"
                fill={colors.text}
                fontSize={12}
                fontWeight="500"
                fontFamily="system-ui, -apple-system, sans-serif"
                className="timeline-label font-medium"
                style={{ 
                  userSelect: 'none',
                  WebkitTextSizeAdjust: 'none',
                  MozTextSizeAdjust: 'none'
                }}
              >
                {label}
              </text>)
            )}
          </g>
        );
      })}
    </Group>
  );
});

export default TimelineGrid; 